import React from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { X, Shield, CheckCircle2 } from 'lucide-react';
import logoLight from '../../assets/brand/logo-light.png';
import logoDark from '../../assets/brand/logo-dark.png';

// Presentational only - deliberately does NOT import AuthContext or
// refreshSession itself. All auth state/handlers live in src/pages/Login.jsx
// and are passed down as props, so the auth flow this modal renders (Google
// ID token -> onLoginSuccess) is provably unchanged by this redesign.
export default function LoginModal({ isDarkTheme, onClose, onLoginSuccess, onLoginError, loginError }) {
  const panelBg = isDarkTheme ? 'bg-navy border-white/10' : 'bg-white border-gray-200';
  const textColor = isDarkTheme ? 'text-white' : 'text-navy';
  const textMuted = isDarkTheme ? 'text-white/60' : 'text-text-slate';

  return (
    // Fixed 2026-09-02 (real tablet bug report, docs/DECISIONS.md): was
    // `inset-0` (the CSS `inset` shorthand, only supported since ~April
    // 2021) - on an older browser that doesn't recognize it, `position:
    // fixed` still applied but with no offsets, so the modal rendered at
    // its natural DOM position (bottom of the page) instead of covering
    // the viewport, making "Login" appear to do nothing. Explicit
    // top/right/bottom/left are individually ancient, universally
    // supported properties - same fix applied to every other `inset-0`
    // site-wide (11 files), not just this one.
    <div className="fixed top-0 right-0 bottom-0 left-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`${panelBg} rounded-dialog border p-8 max-w-md w-full shadow-brand-lg relative`}>
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 ${isDarkTheme ? 'text-white/60 hover:text-white' : 'text-text-slate hover:text-navy'} transition-colors`}
          aria-label="Close login dialog"
        >
          <X size={24} />
        </button>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-5">
            <img src={isDarkTheme ? logoDark : logoLight} alt="RUDVAN" className="h-12 w-auto" />
          </div>
          <h2 className={`text-2xl font-bold ${textColor} mb-2`}>Welcome to RUDVAN</h2>
          <p className={textMuted}>Sign in to start your adaptive learning journey</p>
        </div>

        <div className="flex justify-center mb-6">
          <GoogleLogin
            onSuccess={onLoginSuccess}
            onError={onLoginError}
            theme={isDarkTheme ? 'filled_black' : 'outline'}
            size="large"
          />
        </div>

        {loginError && (
          <p className="text-sm text-red-400 text-center mb-6">{loginError}</p>
        )}

        <div className="relative mb-6">
          <div className="absolute top-0 right-0 bottom-0 left-0 flex items-center">
            <div className={`w-full border-t ${isDarkTheme ? 'border-white/10' : 'border-gray-200'}`} />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className={`px-2 ${isDarkTheme ? 'bg-navy text-white/60' : 'bg-white text-text-slate'}`}>
              Secure & Simple
            </span>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-start gap-3 text-sm">
            <Shield className="text-brand-blue flex-shrink-0 mt-1" size={18} />
            <p className={textMuted}>Your data is encrypted and secure</p>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <CheckCircle2 className="text-brand-blue flex-shrink-0 mt-1" size={18} />
            <p className={textMuted}>No credit card needed to get started</p>
          </div>
        </div>
      </div>
    </div>
  );
}
